import type { SVGProps } from "react";

type MarkProps = SVGProps<SVGSVGElement>;

function mark(props: MarkProps) {
  return {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": true as const,
    ...props,
  };
}

/** Side-on coach — arriving into the city. */
export function BusMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path
        fillRule="evenodd"
        d="M6.6 3h10.8A2.6 2.6 0 0 1 20 5.6v9.2H4V5.6A2.6 2.6 0 0 1 6.6 3Zm.6 2.4h4.2v4H7.2v-4Zm5.6 0h4.2v4h-4.2v-4Z"
      />
      <path d="M3.3 14.9h17.4c.5 0 .9.4.9 1v1.2a1.4 1.4 0 0 1-1.4 1.4h-.6a2.4 2.4 0 0 1-4.7 0H8.1a2.4 2.4 0 0 1-4.7 0h-.5A1.4 1.4 0 0 1 1.5 17v-1.2c0-.6.4-1 .9-1ZM7 18.4a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Zm10 0a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z" />
    </svg>
  );
}

/** International parking sign — P in a circle. */
export function ParkingMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path
        fillRule="evenodd"
        d="M12 1.6a10.4 10.4 0 1 0 0 20.8 10.4 10.4 0 0 0 0-20.8ZM9.6 6.6h3.55c2.35 0 3.95 1.45 3.95 3.55s-1.6 3.55-3.95 3.55h-1.8v4.1H9.6V6.6Zm1.75 1.7v3.7h1.7c1.25 0 2.1-.7 2.1-1.85s-.85-1.85-2.1-1.85h-1.7Z"
      />
    </svg>
  );
}

/** Visitor info — map pin with an i cut out. */
export function TouristMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path
        fillRule="evenodd"
        d="M12 2c4 0 7.2 3.2 7.2 7.1 0 4.8-5.5 10.6-6.8 11.9a.6.6 0 0 1-.9 0C10.3 19.7 4.8 13.9 4.8 9.1 4.8 5.2 8 2 12 2Zm0 4.2a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3ZM11.15 9.8h1.7V16.4h-1.7V9.8Z"
      />
    </svg>
  );
}

/** City tram with pantograph. */
export function TramMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path d="M11.1 2h1.8v2.1h4.2l.8 1.6H6.1l.8-1.6h4.2V2Z" />
      <path
        fillRule="evenodd"
        d="M4.3 6.3h15.4A2.1 2.1 0 0 1 21.8 8.4v7.5a2.1 2.1 0 0 1-2.1 2.1H16.2l.7 1.8h-1.8l-.6-1.8H9.5l-.6 1.8H7.1l.7-1.8H4.3A2.1 2.1 0 0 1 2.2 15.9V8.4A2.1 2.1 0 0 1 4.3 6.3Zm1.6 2.4h5.1v3.6H5.9V8.7Zm7.2 0h5.1v3.6h-5.1V8.7ZM6.3 14.2a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Zm11.4 0a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
      />
    </svg>
  );
}

/** Radiating midsummer sun. */
export function SunMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <circle cx="12" cy="12" r="4.05" />
      <path d="M12 2.15c.5 0 .9.4.9.9v1.6a.9.9 0 1 1-1.8 0V3.05c0-.5.4-.9.9-.9Zm0 16.3c.5 0 .9.4.9.9v1.6a.9.9 0 1 1-1.8 0v-1.6c0-.5.4-.9.9-.9ZM3.05 12c0-.5.4-.9.9-.9h1.6a.9.9 0 1 1 0 1.8H3.95a.9.9 0 0 1-.9-.9Zm16.3 0c0-.5.4-.9.9-.9h1.6a.9.9 0 1 1 0 1.8h-1.6a.9.9 0 0 1-.9-.9ZM5.64 5.64a.9.9 0 0 1 1.27 0l1.13 1.13A.9.9 0 1 1 6.77 8.05L5.64 6.91a.9.9 0 0 1 0-1.27Zm10.32 10.32a.9.9 0 0 1 1.27 0l1.13 1.13a.9.9 0 1 1-1.27 1.27l-1.13-1.13a.9.9 0 0 1 0-1.27ZM18.36 5.64a.9.9 0 0 1 0 1.27l-1.13 1.13a.9.9 0 1 1-1.27-1.27l1.13-1.13a.9.9 0 0 1 1.27 0ZM8.04 15.96a.9.9 0 0 1 0 1.27L6.91 18.36A.9.9 0 1 1 5.64 17.09l1.13-1.13a.9.9 0 0 1 1.27 0Z" />
    </svg>
  );
}

/** Maple / ruska leaf. */
export function LeafMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path d="M12.55 3.05c.35 1.55 1.7 2.95 3.45 3.45 1.55.45 2.6-.15 3.35.7.7.8.3 2.15-.55 3.15 1.35.55 2.2 1.55 2.3 2.7.1 1.35-.9 2.3-2.3 2.7 1.15 1.15 1.35 2.5.35 3.4-.9.8-2.2.45-3.45-.4.2 1.55-.5 2.9-1.75 3.35-.35.15-.75.15-1.05 0-1.25-.45-1.95-1.8-1.75-3.35-1.25.85-2.55 1.2-3.45.4-1-.9-.8-2.25.35-3.4-1.4-.4-2.4-1.35-2.3-2.7.1-1.15 1-2.15 2.3-2.7-.85-1-1.25-2.35-.55-3.15.75-.85 1.8-.25 3.35-.7 1.75-.5 3.1-1.9 3.45-3.45.1-.5.75-.5.85 0Z" />
      <path d="M12 7.4v11.2" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" opacity=".35" />
    </svg>
  );
}

/** Six-arm winter flake. */
export function SnowMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path d="M12 2.2c.45 0 .8.35.8.8v6.15l4.35-4.35a.8.8 0 1 1 1.13 1.13L13.93 10.3h6.07a.8.8 0 1 1 0 1.6h-6.07l4.35 4.35a.8.8 0 1 1-1.13 1.13L12.8 12.95V19a.8.8 0 1 1-1.6 0v-6.05L6.85 17.3A.8.8 0 1 1 5.72 16.17L10.07 11.9H4a.8.8 0 1 1 0-1.6h6.07L5.72 5.95A.8.8 0 1 1 6.85 4.82L11.2 9.15V3c0-.45.35-.8.8-.8Z" />
      <circle cx="12" cy="11.1" r="1.55" />
    </svg>
  );
}

/** First spring shoot. */
export function SproutMark(props: MarkProps) {
  return (
    <svg {...mark(props)}>
      <path d="M11.15 12.6h1.7v7.7c0 .5-.4.9-.85.9s-.85-.4-.85-.9v-7.7Z" />
      <path d="M12 13.4C8.4 13.1 5.8 10.6 5.4 7.4c-.2-1.5 1.1-2.6 2.5-2.2 2.9.8 4 3.6 4.1 6.6v1.6Z" />
      <path d="M12 13.4c3.6-.3 6.2-2.8 6.6-6 .2-1.5-1.1-2.6-2.5-2.2-2.9.8-4 3.6-4.1 6.6v1.6Z" />
    </svg>
  );
}
